import { useEffect } from 'react'

/**
 * Generates FAQPage structured data (JSON-LD) directly from the visible
 * .faq-item elements already rendered on the page, instead of duplicating
 * the question/answer text by hand in a separate schema object.
 *
 * Why read from the DOM rather than pass the data in as a prop/argument:
 * Google requires FAQ schema to match the page's visible content exactly.
 * Reading the live DOM guarantees that match automatically — if a FAQ
 * question or answer is ever edited in the JSX, the schema updates itself
 * with zero risk of drifting out of sync.
 *
 * Expects the existing markup pattern used across tool pages:
 *   <div className="faq-item">
 *     <h3>Question text</h3>
 *     <p>Answer text</p>
 *   </div>
 */
export default function useFAQSchema() {
    useEffect(() => {
        const items = document.querySelectorAll('.faq-item')
        if (items.length === 0) return

        const mainEntity = Array.from(items)
            .map(item => {
                const question = item.querySelector('h3')?.textContent?.trim()
                const answer = item.querySelector('p')?.textContent?.trim()
                if (!question || !answer) return null
                return {
                    '@type': 'Question',
                    name: question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: answer,
                    },
                }
            })
            .filter(Boolean)

        if (mainEntity.length === 0) return

        const structuredData = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity,
        }

        let script = document.querySelector('script[data-schema="faq"]')
        if (!script) {
            script = document.createElement('script')
            script.type = 'application/ld+json'
            script.setAttribute('data-schema', 'faq')
            document.head.appendChild(script)
        }
        script.textContent = JSON.stringify(structuredData)

        // Remove on unmount — this is an SPA, so navigating to a page without
        // FAQ content must not leave a stale FAQ schema pointing at content
        // that no longer exists on screen.
        return () => {
            const stale = document.querySelector('script[data-schema="faq"]')
            if (stale) stale.remove()
        }
    }, [])
}